import { z } from 'zod';

/**
 * Matches a Go `time.Duration` string, e.g. used by Nebula config values like `punchy.delay`.
 * Mirrors the units accepted by Go's `time.ParseDuration`: ns, us (or µs), ms, s, m, h.
 * Supports sub-second values ("10ms", "0.1s") and combinations ("1m30s").
 */
const GO_DURATION_RE = /^(?:(?:\d+\.?\d*|\.\d+)(?:ns|us|µs|ms|s|m|h))+$/;

export const INVALID_GO_DURATION_ERR = 'Enter a duration with units, e.g. 100ms, 0.5s, 1s, or 0s';

/** Whether a string is a valid Go duration. A bare "0" is also allowed, matching Go. */
export function isValidGoDuration(value: string): boolean {
  return value === '0' || GO_DURATION_RE.test(value);
}

/**
 * Schema for a Go duration string. Unlike {@link durationSchema}, this keeps sub-second precision
 * ("10ms", "0.1s") and allows zero ("0s").
 *
 * A bare non-negative integer is treated as seconds (e.g. "5" -> "5s") for convenience, matching
 * the second-resolution behavior offered elsewhere. Empty string and null are allowed so the field
 * can be cleared.
 */
export const goDurationSchema = z
  .string()
  .trim()
  .transform((value) => (/^\d+$/.test(value) ? `${value}s` : value))
  .superRefine((value, ctx) => {
    if (value !== '' && !isValidGoDuration(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: INVALID_GO_DURATION_ERR,
        fatal: true,
      });
    }
  })
  .nullable()
  .or(z.literal(''));
